/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { IconDotsVertical, IconX, IconCheck } from "@tabler/icons-react";
import ViewRequestModal from "./ViewRequestModal";
import formatDate from "../../../utilities/helper";
import {
  useReAssignRequestListData,
  useVehicleReAssignMutation,
} from "../../../apis/fleetApis";
import { useForm } from "react-hook-form";
import ChangeVehicleModal from "./ChangeVehicleModal";
import toast from "react-hot-toast";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import { T, useLanguage } from "../../../context/LanguageContext";
import useDebounce from "../../../utilities/useDebounce";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { useSelector } from "react-redux";
import CardSkeleton from "../../../components/skeleton/CardSkeleton";
import { usePermission } from "../../../hooks/usePermissionTabs";

type RequestData = {
  id: string;
};
type FormValues = {
  driverId: string;
  vehicleId: string;
  lastOdometer: string;
  inspectionPaper: string;
};

type RequestsProps = {
  vehicleId?: string;
};

const RequestsVehiclesManagement: React.FC<RequestsProps> = ({ vehicleId }) => {
  const { t } = useLanguage();
  const { allowed: canUpdateVehicle } = usePermission("vehicles", "update");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [searchFor, setSearchFor] = useState<string>("");
  const [openChangeVehicle, setOpenChangeVehicle] = useState<boolean>(false);
  const openChangeVehicleToggleModal = () => {
    setOpenChangeVehicle(!openChangeVehicle);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [viewDetailsItem, setViewDetailsItem] = useState([]);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<RequestData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);

  const openModal = (item: any) => {
    setViewDetailsItem(item);
    setIsOpen(true);
  };
  const closeModal = () => setIsOpen(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { refetch, isLoading, isFetching } = useReAssignRequestListData(
    pageNo,
    limit,
    searchFor,
    vehicleId,
  );

  const data = useSelector((state: any) => state?.changeRequest?.data);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  useEffect(() => {
    if (data?.vehicles) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item: any) =>
          selectedRows.includes(item.id),
        );
        const newSelected = data.vehicles.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [data?.vehicles, selectedRows]);

  const debouncedSearchFor = useDebounce(searchFor, 550);

  useEffect(() => {
    refetch();
  }, [debouncedSearchFor, pageNo, limit]);

  const {
    register: assignRegister,
    handleSubmit: handleAssignSubmit,
    clearErrors: clearAssignErrors,
    reset: assignReset,
    setValue: setAssignValue,
    watch: assignWatch,
    control: assignControl,
    formState: { errors: assignErrors },
  } = useForm<FormValues>();

  const [driverData, setDriverData] = useState(data);
  const { mutate: vehicleReAssign, isPending: changePending } =
    useVehicleReAssignMutation();
  const handleVehicleReAssign = (data: any) => {
    vehicleReAssign(
      { ...data, driverId: driverData?.driver_id },
      {
        onSuccess: () => {
          openChangeVehicleToggleModal();
          assignReset();
          refetch();
        },
        onError: (error) => {
          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            (typeof error === "string" ? error : null) ||
            "Failed to Update Vehicle";

          toast.error(errorMessage);
        },
      },
    );
  };

  useEffect(() => {
    if (data) {
      setDriverData(data);
    }
  }, [data]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [columnsData, setColumnsData] = useState([
    { text: "Checkbox", value: "checkbox", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Driver Name", value: "drivers_name", isShow: true },
    { text: "Vehicle Name", value: "vehicles_name", isShow: true },
    { text: "Vehicle Number", value: "vehicles_number", isShow: true },
    { text: "RC Number", value: "vehicles_rc", isShow: true },
    { text: "Make & Model", value: "vehicles_model", isShow: true },
    { text: "Type", value: "vehicles_type", isShow: true },
    { text: "Date & Time", value: "request_date", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);

  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === data?.vehicles?.length) {
      setSelectedRows((prev) =>
        prev.filter((id) => !data.vehicles.some((c: any) => c.id === id)),
      );
    } else {
      const newSelections =
        data?.vehicles
          ?.filter((c: any) => !selectedRows.includes(c.id))
          .map((c: any) => c.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "checkbox":
          return {
            label: (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    data?.vehicles?.length > 0 &&
                    data?.vehicles?.every((c: any) =>
                      selectedRows.includes(c.id),
                    )
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                 bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                  top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
            render: (c: any) => (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRows.includes(c.id)}
                  onChange={() => handleSelectRow(c.id)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                  bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
          };
        case "sn":
          return {
            label: "S/N",
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "drivers_name":
          return {
            label: "Driver Name",
            render: (c: any) => c.drivers_name,
          };
        case "vehicles_name":
          return {
            label: "Vehicle Name",
            render: (c: any) => c.vehicles_name,
          };
        case "vehicles_number":
          return {
            label: "Vehicle Number",
            render: (c: any) => c.vehicles_number,
          };
        case "vehicles_rc":
          return {
            label: "RC Number",
            render: (c: any) => c.vehicles_rc,
          };
        case "vehicles_model":
          return {
            label: "Make & Model",
            render: (c: any) => c.vehicles_model,
          };
        case "vehicles_type":
          return { label: "Type", render: (c: any) => c.vehicles_type };
        case "request_date":
          return {
            label: "Date & Time",
            render: (c: any) => formatDate(c.request_date),
          };
        case "status":
          return {
            label: "Status",
            render: (c: { is_assigned: any }) => {
              const isAssigned = c.is_assigned;
              const status = isAssigned === true ? "Changed" : "Pending";
              const statusClasses =
                isAssigned === true
                  ? "bg-[#CDF3E5] border-[#43A581] text-[#212121]"
                  : "bg-[#FF4848]/10 border-[#EC221F] text-[#212121]";
              return (
                <div className="flex items-center justify-center">
                  <button
                    className={`border rounded-full text-xs font-medium w-28 px-4 py-1.5 text-center ${statusClasses}`}
                  >
                    {status}
                  </button>
                </div>
              );
            },
          };
        case "actions":
          return {
            label: "Actions",
            render: (s: any) => (
              <div className="relative text-center">
                <button
                  onClick={() =>
                    setOpenDropdownIndex(openDropdownIndex === s ? null : s)
                  }
                  className="p-2 border rounded-full bg-[#F7F6F2]"
                >
                  <IconDotsVertical size={22} />
                </button>

                {openDropdownIndex === s && (
                  <div
                    ref={dropdownRef}
                    className="fixed bg-[#F7F6F2] divide-y text-left z-[999] w-fit whitespace-nowrap rounded-xl shadow-lg overflow-hidden"
                    style={{
                      transform: "translateX(-80%)",
                    }}
                  >
                    <div
                      onClick={() => {
                        openModal(s);
                      }}
                      className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer"
                    >
                      {t("View Requests")}
                    </div>
                    {!s.is_assigned && canUpdateVehicle && (
                      <div
                        onClick={() => {
                          openChangeVehicleToggleModal();
                          setDriverData(s);
                        }}
                        className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer"
                      >
                        {t("Change Vehicle")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ),
          };
        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "Driver Name": c.drivers_name,
    "Vehicle Name": c.vehicles_name,
    "Vehicle Number": c.vehicles_number,
    "RC Number": c.vehicles_rc,
    "Make & Model	": c.vehicles_model,
    Type: c.vehicles_type,
    Date: formatDate(c.request_date),
    Status: c.is_assigned,
  });

  return (
    <>
      <div>
        {!openChangeVehicle ? (
          <>
            {/* Change Requests List */}
            <div className="flex items-center justify-between px-2 py-3 w-full">
              <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
                <T>Change Requests</T>
              </h1>

              {data && data.totalPages * limit > 10 && (
                <EntriesSelector
                  value={limit}
                  options={[
                    10,
                    ...(data.totalPages * limit > 10 ? [20] : []),
                    ...(data.totalPages * limit > 20 ? [30] : []),
                    ...(data.totalPages * limit > 50 ? [50] : []),
                    ...(data.totalPages * limit > 100 ? [100] : []),
                  ]}
                  onChange={(val) => {
                    setLimit(val);
                    setPageNo(1);
                  }}
                />
              )}
            </div>

            {/* Your existing list content */}
            <div>
              {isLoading || isFetching ? (
                <CardSkeleton rows={3} />
              ) : data?.vehicles && data?.vehicles.length > 0 ? (
                <>
                  {data.vehicles.map((v: any, idx: number) => (
                    <div
                      key={v.id || idx}
                      className="rounded-2xl bg-white border border-gray-200 p-4 shadow-sm transition-all hover:shadow-md flex flex-col md:flex-row md:items-center justify-between gap-4 w-full mb-3"
                    >
                      <div className="space-y-2 flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-[#212121] font-bold text-base sm:text-lg truncate max-w-[180px] sm:max-w-xs">
                            {v.vehicles_name || v.vehicles_model || t("Vehicle")}
                          </h3>

                          <span className="bg-indigo-50 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded border border-indigo-100 shrink-0">
                            {v.vehicles_number || "V-000"}
                          </span>

                          <span className="bg-amber-50 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded border border-amber-200 shrink-0">
                            {t(v.reason || "Medium Urgency")}
                          </span>
                        </div>

                        <p className="text-gray-600 text-sm leading-relaxed break-words">
                          {v.describe_reason ||
                            t("Change request submitted for {vehicle} assignment by driver {driver}.")
                              .replace("{vehicle}", v.vehicles_model || t("vehicle"))
                              .replace("{driver}", v.drivers_name || t("Unknown Driver"))}
                        </p>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                          <p>
                            <T>Submitted by</T>:{" "}
                            <span className="text-[#212121] font-bold">
                              {v.drivers_name || t("Unknown Driver")}
                            </span>
                          </p>

                          <span className="hidden sm:inline text-gray-300">
                            •
                          </span>

                          <p>
                            <T>Date Filed</T>:{" "}
                            <span className="text-[#212121] font-bold">
                              {formatDate(v.request_date)}
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between md:justify-end gap-4 border-t md:border-t-0 border-gray-100 pt-3 md:pt-0 shrink-0">
                        <div className="text-start md:text-end">
                          <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">
                            <T>STATUS</T>
                          </p>

                          <p
                            className={`font-semibold text-sm ${v.is_assigned ? "text-[#43A581]" : "text-[#FBD037]"}`}
                          >
                            {v.is_assigned ? t("Approved") : t("Pending")}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          {!v.is_assigned && (
                            <>
                              <button
                                className="w-8 h-8 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-colors"
                                title="Reject Request"
                              >
                                <IconX size={18} />
                              </button>

                              <button
                                onClick={() => {
                                  openChangeVehicleToggleModal();

                                  setDriverData(v);
                                }}
                                className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${v.is_assigned ? "bg-emerald-50 border border-emerald-200 text-emerald-600" : "bg-gray-50 border border-gray-200 text-gray-500 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"}`}
                                title="Approve Request"
                              >
                                <IconCheck size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <NoDataFound mainClassName="" subClassName="" />
              )}
            </div>

            <div className="py-4 flex justify-center">
              {data?.totalPages > 1 && (
                <Pagination
                  totalPages={data?.totalPages}
                  currentPage={pageNo}
                  fetchData={handlePaginationFetch}
                />
              )}
            </div>
          </>
        ) : (
          <>
            {/* Change Vehicle Panel */}
            <div className="w-full border bg-white rounded-xl overflow-y-auto flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h2 className="text-lg font-semibold"><T>Change Vehicle</T></h2>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={changePending}
                    onClick={handleAssignSubmit(handleVehicleReAssign)}
                    className="px-2.5 sm:px-4 py-1.5 sm:py-2 border border-[#FBD037] text-[#FBD037] bg-white text-xs sm:text-sm font-semibold rounded-lg hover:bg-[#FBD037]/10 transition-colors"
                  >
                    {changePending ? t("Changing...") : t("Change")}
                  </button>

                  <button
                    onClick={() => {
                      openChangeVehicleToggleModal();
                      assignReset();
                    }}
                    className="p-1.5 sm:p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
                  >
                    <IconX size={18} />
                  </button>
                </div>
              </div>

              <div className="p-4">
                <ChangeVehicleModal
                  register={assignRegister}
                  errors={assignErrors}
                  setValue={setAssignValue as any}
                  watch={assignWatch}
                  clearErrors={clearAssignErrors as any}
                  control={assignControl}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <ViewRequestModal
        vehicleDetails={viewDetailsItem}
        open={isOpen}
        onClose={closeModal}
      />
    </>
  );
};

export default RequestsVehiclesManagement;
